import { z } from 'zod';
/**
 * Sites, places, and vendor waypoint aliases (DEV-17247, memory-architecture
 * §5.3) — symbolic localisation, phase 1. Names are the shared vocabulary
 * between the orchestrator and every vendor stack; coordinates stay local to
 * each robot. No coordinate math exists anywhere in this contract, by design:
 * "go to the dock" resolves a NAME in each robot's private frame, the same
 * way VDA 5050 references nav-graph node ids and never metric positions.
 *
 * `Site` resolves `SpecSection.location.mapId` by convention (mapId =
 * Site.id). `Place` supplies the place anchors and the tree distance the
 * anchored-retrieval scorer uses (DEV-17257). `PlaceAlias` is the onboarding
 * primitive: a vendor robot's own waypoint name mapped onto our Place — a
 * config screen, not a survey visit. PlaceEdge (directed traversal edges) is
 * deferred until the routing need is evidenced (DEV-17259).
 */
export const SiteKindSchema = z.enum(['INDOOR', 'OUTDOOR', 'MIXED']);
/**
 * The place kinds v1 ships. A free vocabulary at the DB level (string
 * column) so a customer taxonomy can extend it without a migration; the
 * create input restricts to the known set so a typo is a 400, not a silently
 * unmatchable kind. Exclusion zones are ordinary places with kind 'no_go'.
 */
export const placeKinds = ['zone', 'room', 'aisle', 'dock', 'no_go', 'field_block'];
export const PlaceKindSchema = z.enum(placeKinds);
// ─── Geo + coordinate value types (shared by entities, inputs, and dispatch) ───
/**
 * A geodetic point: latitude/longitude in degrees (WGS84), optional altitude in
 * metres. `alt` is an absolute GNSS altitude as surveyed or reported by the
 * robot (its own vertical reference); it is never derived from a site-frame z,
 * which is relative to the site datum (see SiteFrameZSchema).
 */
export const GeoPointSchema = z.object({
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    alt: z.number().optional(),
});
/**
 * Largest |latitude| (degrees) a geo-anchor may sit at. Site-frame <-> GPS conversion
 * divides by cos(latitude) to turn east metres into longitude; that blows up toward the
 * poles, so an anchor past this is rejected rather than converted to a wrong longitude.
 */
export const maxGeoAnchorAbsLatDeg = 89.9;
/** A Site's GPS registration: the geodetic position of site (0,0) and the bearing of site +X (CCW from East, radians). */
export const GeoAnchorSchema = z.object({
    lat: z
        .number()
        .min(-maxGeoAnchorAbsLatDeg, {
        message: `geoAnchor.lat must be within +/-${maxGeoAnchorAbsLatDeg} degrees (polar anchors are not supported)`,
    })
        .max(maxGeoAnchorAbsLatDeg, {
        message: `geoAnchor.lat must be within +/-${maxGeoAnchorAbsLatDeg} degrees (polar anchors are not supported)`,
    }),
    lon: z.number().min(-180).max(180),
    headingRad: z.number(),
});
/**
 * Site-frame z (AC-8, aerial goals). Metres ABOVE THE SITE DATUM: the site
 * frame's own z = 0 plane (the floor of level 0 indoors, the geo-anchor's
 * ground point outdoors). It is NOT an ellipsoid (WGS84) height and NOT a
 * height above mean sea level, so it is never copied into a GPS `alt`. Absent
 * means a ground goal (2D). Registrations are planar (SE(2)), so z passes
 * through a site <-> robot transform unchanged.
 */
export const SiteFrameZSchema = z.number().finite();
/**
 * A pose in the site frame: position plus optional heading (radians) and, for
 * aerial goals, optional site-frame z (see SiteFrameZSchema). Without z it is
 * the 2D pose ground robots have always used.
 */
export const SiteFramePoseSchema = z.object({
    x: z.number(),
    y: z.number(),
    yaw: z.number().optional(),
    z: SiteFrameZSchema.optional(),
});
// ─── Dispatch goals (MAP-3 / AC-8) ───────────────────────────────────────────
/** A pose in ONE robot's own frame: what a metric goto takes. z only for aerial goals. */
export const RobotFramePoseSchema = z.object({
    x: z.number(),
    y: z.number(),
    yaw: z.number().optional(),
    /** Metres above the shared vertical datum (planar registration: same as the site-frame z). */
    z: z.number().optional(),
});
/**
 * The goal a dispatch should send to one robot for one Place (resolvePlaceToGoal).
 * 2D results are unchanged by AC-8: `pose.z` and `altAboveSiteDatum` appear only
 * when the Place carries a site-frame z.
 */
export const ResolvedGoalSchema = z.discriminatedUnion('kind', [
    z.object({
        /** The robot physically drove to and saved this point; most accurate, transform-independent. */
        kind: z.literal('native'),
        waypointId: z.string(),
    }),
    z.object({
        /** An ad-hoc site point pushed through the robot's transform into its own frame. */
        kind: z.literal('transformed'),
        pose: RobotFramePoseSchema,
        /** `live` or `stale`; `stale` means show a caution before sending. */
        status: z.enum(['live', 'stale']),
    }),
    z.object({
        /** A GPS goal: an outdoor waypoint, or a robot without a usable metric transform outdoors. */
        kind: z.literal('geo'),
        lat: z.number(),
        lon: z.number(),
        /** Absolute GNSS altitude, only when the Place's geoPoint carries one (never derived from z). */
        alt: z.number().optional(),
        /**
         * The Place's site-frame z: metres above the site datum. A relative height
         * (like MAVLink relative_alt), not an ellipsoid height. Present only when the
         * Place has a z, so an aerial GPS goal keeps its altitude.
         */
        altAboveSiteDatum: z.number().optional(),
        /**
         * True when this GPS point was DERIVED from the Place's site pose via the
         * Site's WGS84 geo-anchor (outdoor sites only), not a surveyed geoPoint.
         */
        derived: z.boolean().optional(),
    }),
]);
/**
 * POST /v1/places/resolve-goal: which goal should this robot get for this Place?
 * Name lookup matches a Place label (case-insensitive) or a live alias in the
 * device's own scope or 'fleet'. Pass siteId to disambiguate across sites.
 */
export const ResolvePlaceGoalInputSchema = z
    .object({
    deviceId: z.string().min(1).max(200),
    placeId: z.string().min(1).max(200).optional(),
    placeName: z.string().min(1).max(200).optional(),
    siteId: z.string().min(1).max(200).optional(),
})
    .refine((v) => (v.placeId === undefined) !== (v.placeName === undefined), {
    message: 'Pass exactly one of placeId or placeName',
    path: ['placeId'],
});
/**
 * The registered command dispatch-options would send one robot to one Place
 * with: `nav` is the registration's navCommand with the robot's own (or
 * tag-bound) waypoint value verbatim, `dock` is its dockCommand with an empty
 * parameter for its own dock, `pose` is its poseCommand with the place pushed
 * through a current alignment (approximate, about `errorM` metres). Pass it
 * through unchanged.
 */
export const PlaceDispatchCommandSchema = z.object({
    kind: z.enum(['nav', 'dock', 'pose']),
    templateName: z.string(),
    parameter: z.string(),
    /** Approximate goals only: the alignment's error, metres. */
    errorM: z.number().nonnegative().optional(),
    /** A caution dispatch-options attached to an eligible option (e.g. no heading). */
    caution: z.string().optional(),
});
export const PlaceGoalResolutionSchema = z.object({
    place: z.object({ id: z.string(), label: z.string(), siteId: z.string() }),
    site: z.object({ id: z.string(), kind: SiteKindSchema }),
    goal: ResolvedGoalSchema,
    /**
     * The robot's own name for this Place (a live alias scoped to the deviceId),
     * when one exists. A robot that advertises `goto_place` can be sent this name
     * and resolve it in its own map, which beats a transformed pose.
     */
    deviceAlias: z.string().nullable(),
    /**
     * Present when dispatch-options marks this robot eligible for this Place with
     * a registered command (navCommand or dockCommand). Callers send this first,
     * before falling back to what the robot advertises in robot.capabilities.
     */
    dispatchCommand: PlaceDispatchCommandSchema.optional(),
});
// ─── Formant tags (place labels + tag-binding selectors, fleet-map design F3/F7) ───
/**
 * Formant's tag key/value rule (`@formant/core` isValidTagString, admin-api
 * TagsConstraint): 1-255 characters from a fixed set. Device tags and place tags
 * share it so the same picker works for both.
 */
export const formantTagStringPattern = /^[a-zA-Z0-9-_.,:?'"()@/#$+ ]{1,255}$/;
/** At most this many keys in a place's tags or a binding selector (admin-api allows 30 per device). */
export const maxTagKeys = 30;
/** At most this many values for one key in a tag set. */
export const maxTagValuesPerKey = 50;
/** `*` as a selector value: the key is present with any value (Formant `anyTagValue`). */
export const anyTagValue = '*';
/** `""` as a selector value: the key is absent (Formant `notTagKey`). */
export const absentTagValue = '';
const TagKeySchema = z.string().regex(formantTagStringPattern, {
    message: 'Tag keys must be 1-255 characters of letters, digits, spaces or -_.,:?\'"()@/#$+',
});
const TagValueSchema = z.string().regex(formantTagStringPattern, {
    message: 'Tag values must be 1-255 characters of letters, digits, spaces or -_.,:?\'"()@/#$+',
});
/**
 * Formant `ITagSets`: key -> allowed values. As a filter it means AND across keys,
 * OR within a key's values (`@formant/core` matchesTags). Each key has at least
 * one value, which is never an empty list (admin-api rejects those).
 */
export const PlaceTagSetsSchema = z
    .record(TagKeySchema, z.array(TagValueSchema).min(1).max(maxTagValuesPerKey))
    .refine((v) => Object.keys(v).length <= maxTagKeys, {
    message: `At most ${maxTagKeys} tag keys`,
});
/**
 * A tag-binding selector: an `ITagSets` filter over device tags. Non-empty (an
 * empty selector would match every robot in the org). A value may also be `*`
 * (key present, any value) or `""` (key absent), as in Formant's filter syntax.
 */
export const TagSelectorSchema = z
    .record(TagKeySchema, z
    .array(z.union([TagValueSchema, z.literal(anyTagValue), z.literal(absentTagValue)]))
    .min(1)
    .max(maxTagValuesPerKey))
    .refine((v) => Object.keys(v).length >= 1, { message: 'The selector needs at least one key' })
    .refine((v) => Object.keys(v).length <= maxTagKeys, {
    message: `At most ${maxTagKeys} selector keys`,
});
/**
 * A device's Formant tags (`ITags`): one string value per key. An empty value
 * means no value, so it is dropped on parse (the key then reads as absent).
 */
export const DeviceTagsSchema = z
    .record(z.string(), z.string())
    .transform((tags) => Object.fromEntries(Object.entries(tags).filter(([, v]) => v !== '')));
/**
 * Formant tag matching for one device against one selector: every selector key
 * must match (AND); a key matches when the device's value is one of the listed
 * values (OR), when `*` is listed and the key is present, or when `""` is listed
 * and the key is absent. Same rules as `@formant/core` matchesTags for a single
 * `ITags` against an `ITagSets` filter, without the reserved `**` key. An empty
 * selector matches nothing here, unlike matchesTags, because a binding that
 * applies to every robot is never what someone meant.
 */
export function matchesTagSelector(deviceTags, selector) {
    const entries = Object.entries(selector);
    if (entries.length === 0)
        return false;
    return entries.every(([key, values]) => {
        // An empty value is no value: the key reads as absent.
        const value = Object.prototype.hasOwnProperty.call(deviceTags, key) ? deviceTags[key] : '';
        if (value === undefined || value === '')
            return values.includes(absentTagValue);
        if (values.includes(anyTagValue))
            return true;
        return values.includes(value);
    });
}
// ─── Entities ────────────────────────────────────────────────────────────────
export const SiteSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: z.string(),
    kind: SiteKindSchema,
    /** Optional GPS anchor of the site frame (MAP-5); null on a purely-indoor site. */
    geoAnchor: GeoAnchorSchema.optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
});
export const PlaceSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    siteId: z.string(),
    parentPlaceId: z.string().nullable(),
    /** Serialized wide (string): rows may predate a narrowed create vocabulary. */
    kind: z.string(),
    label: z.string(),
    /** Coordinate layer (MAP-1/MAP-5), additive + optional; symbolic-only consumers ignore it. */
    siteFramePose: SiteFramePoseSchema.optional(),
    geoPoint: GeoPointSchema.optional(),
    perDeviceNativeWaypointId: z.record(z.string(), z.string()).optional(),
    /** Floor of the site this place is on (multi-floor). 0 for a single-level site. */
    level: z.number().int().optional(),
    /** Free labels on the place, e.g. { task: ['inspection'] } (fleet-map design F3). */
    tags: PlaceTagSetsSchema.optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
});
export const PlaceAliasSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    placeId: z.string(),
    /** Whose vocabulary: a deviceId, a vendor slug, or 'fleet'. */
    scope: z.string(),
    alias: z.string(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
});
// ─── Inputs ──────────────────────────────────────────────────────────────────
export const CreateSiteInputSchema = z.object({
    name: z.string().min(1).max(200),
    kind: SiteKindSchema,
    geoAnchor: GeoAnchorSchema.optional(),
});
export const UpdateSiteInputSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    kind: SiteKindSchema.optional(),
    geoAnchor: GeoAnchorSchema.nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export const CreatePlaceInputSchema = z.object({
    siteId: z.string().min(1),
    parentPlaceId: z.string().min(1).optional(),
    kind: PlaceKindSchema,
    label: z.string().min(1).max(200),
    siteFramePose: SiteFramePoseSchema.optional(),
    geoPoint: GeoPointSchema.optional(),
    perDeviceNativeWaypointId: z.record(z.string(), z.string()).optional(),
    /** Floor of the site (default 0). */
    level: z.number().int().min(-100).max(1000).optional(),
    tags: PlaceTagSetsSchema.optional(),
});
export const UpdatePlaceInputSchema = z
    .object({
    parentPlaceId: z.string().min(1).nullable().optional(),
    kind: PlaceKindSchema.optional(),
    label: z.string().min(1).max(200).optional(),
    siteFramePose: SiteFramePoseSchema.nullable().optional(),
    geoPoint: GeoPointSchema.nullable().optional(),
    perDeviceNativeWaypointId: z.record(z.string(), z.string()).nullable().optional(),
    level: z.number().int().min(-100).max(1000).optional(),
    /** `null` clears the place's tags. */
    tags: PlaceTagSetsSchema.nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export const CreatePlaceAliasInputSchema = z.object({
    placeId: z.string().min(1),
    scope: z.string().min(1).max(200),
    alias: z.string().min(1).max(200),
});
// ─── Tag bindings (fleet-map design F7, DEV-17679) ──────────────────────────
/** A goto parameter, sent verbatim (a waypoint name, or `waypoint_id=<GraphNav id>`). */
export const NativeWaypointIdSchema = z.string().trim().min(1).max(500);
/**
 * Every robot whose Formant tags match `selector` reaches this place by sending
 * `nativeWaypointId` (on a dock place: by its dock command, and the binding marks
 * the place as those robots' dock). A per-device entry in
 * `perDeviceNativeWaypointId` always wins over a binding. Used for a tag group
 * that shares one navigation map and therefore one waypoint id.
 */
export const PlaceTagBindingSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    placeId: z.string(),
    selector: TagSelectorSchema,
    /** The shared map's goto parameter. Absent on a dock place. */
    nativeWaypointId: z.string().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
});
/** POST /v1/places/:placeId/tag-bindings. `nativeWaypointId` is required unless the place is a dock. */
export const CreatePlaceTagBindingInputSchema = z.object({
    selector: TagSelectorSchema,
    nativeWaypointId: NativeWaypointIdSchema.optional(),
});
/** PATCH /v1/places/:placeId/tag-bindings/:id. Omit a key to leave it. */
export const UpdatePlaceTagBindingInputSchema = z
    .object({
    selector: TagSelectorSchema.optional(),
    /** `null` clears it (only valid on a dock place). */
    nativeWaypointId: NativeWaypointIdSchema.nullable().optional(),
})
    .refine((v) => v.selector !== undefined || v.nativeWaypointId !== undefined, {
    message: 'Nothing to update',
});
// ─── Alias onboarding (the vendor waypoint import flow) ─────────────────────
/**
 * POST body for the alias auto-proposal step: the vendor robot's own
 * waypoint list, verbatim. The service matches each name against the site's
 * Places by NAME SIMILARITY only (normalized token overlap + edit distance —
 * deterministic, zero model calls) and returns ranked proposals for a person
 * to confirm. Nothing is written until the confirm call.
 */
export const ProposePlaceAliasesInputSchema = z.object({
    siteId: z.string().min(1),
    /** Whose vocabulary the aliases will belong to (deviceId / vendor / 'fleet'). */
    scope: z.string().min(1).max(200),
    waypoints: z.array(z.string().min(1).max(200)).min(1).max(500),
});
export const PlaceAliasProposalSchema = z.object({
    /** The vendor waypoint name being matched. */
    alias: z.string(),
    /** Best-match place id, or null when nothing clears the floor. */
    placeId: z.string().nullable(),
    placeLabel: z.string().nullable(),
    /** Similarity in [0,1]; a person confirms — this is a hint, not a decision. */
    confidence: z.number(),
    /** True when an alias row for (scope, alias) already exists. */
    alreadyMapped: z.boolean(),
});
/** Confirm step: exactly the proposals the person accepted. */
export const ConfirmPlaceAliasesInputSchema = z.object({
    siteId: z.string().min(1),
    scope: z.string().min(1).max(200),
    mappings: z
        .array(z.object({ alias: z.string().min(1).max(200), placeId: z.string().min(1) }))
        .min(1)
        .max(500),
});
// ── MAP-1 (DEV-17527): coordinate + registration layer on top of the symbolic Site/Place above.
//    Additive; the symbolic schemas stay coordinate-free. See unified-fleet-map-design.md.
/**
 * site_T_robot as a 2D similarity. Apply as site = scale * R(rotationRad) * robot + (tx, ty).
 * scale is 1 for rigid (metric) robots; only non-1 for scale-ambiguous sources.
 */
export const SiteTransform2DSchema = z.object({
    rotationRad: z.number(),
    scale: z.number().positive(),
    tx: z.number(),
    ty: z.number(),
});
/** One physical location expressed in both the site frame and a robot's own frame. */
export const CorrespondenceSchema = z.object({
    /** The canonical Place this control point is (optional; ad-hoc points may have none). */
    placeId: z.string().optional(),
    site: z.object({ x: z.number(), y: z.number() }),
    robot: z.object({ x: z.number(), y: z.number() }),
});
/** How a device's transform into the site frame was established. */
export const RegistrationMethodSchema = z.enum([
    'fiducial', // surveyed AprilTag / ArUco
    'geo_datum', // geo-anchored GNSS
    'prior_map', // relocalization against a saved map
    'control_points', // shared named waypoints driven on the robot
    'manual', // operator drag/rotate/scale in the UI
    'map_merge', // automatic feature-based grid merge
]);
/** Trustworthiness of a device's current transform. */
export const RegistrationStatusSchema = z.enum([
    'live', // fresh and trustworthy
    'stale', // past its freshness budget; show with a caution
    'needs_reanchor', // a frame reset/jump invalidated it
    'unregistered', // no transform yet; the robot cannot be placed on the map
]);
/**
 * The per-device registration into a site: the transform plus the evidence it was
 * solved from (so it is auditable and re-solvable), a confidence, and a status.
 * The home for this is cortex, keyed by (siteId, deviceId); see DEV-17241.
 */
export const DeviceSiteRegistrationSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    siteId: z.string(),
    deviceId: z.string(),
    /** Which site level this registration is for (multi-floor); null/absent for single-level. */
    level: z.number().int().optional(),
    transform: SiteTransform2DSchema,
    method: RegistrationMethodSchema,
    /** The correspondences the transform was solved from (RMF reference_coordinates pattern). */
    correspondences: z.array(CorrespondenceSchema),
    /** Fit residual (RMSE, site units) and inlier count, for trust/debug. */
    rmse: z.number().nonnegative().optional(),
    inlierCount: z.number().int().nonnegative().optional(),
    /** Isotropic 1-sigma position uncertainty (site units), inflated with time/distance since last fix. */
    positionSigma: z.number().nonnegative().optional(),
    status: RegistrationStatusSchema,
    /**
     * True = this transform does not age: its status is never downgraded to
     * `stale` by time since updatedAt (a sim robot's identity registration
     * cannot drift). Absent/false = ages normally. Set only via the manual
     * registration body.
     */
    pinned: z.boolean().optional(),
    /**
     * The command template name that sends this robot to one of its own saved
     * waypoints (design D3). The parameter is the Place's
     * perDeviceNativeWaypointId[deviceId]. Absent = not dispatchable to a Place yet.
     */
    navCommand: z.string().optional(),
    /**
     * The command template that docks this robot (sent with an empty parameter) when
     * it is dispatched to a `dock` place that is its own dock. Absent = cannot be
     * sent to its dock from a Place yet.
     */
    dockCommand: z.string().optional(),
    /**
     * The command template that sends this robot to coordinates in its OWN frame
     * (any-robot-any-place design, rule 2), e.g. `spot.graphnav.goto_pose`.
     */
    poseCommand: z.string().optional(),
    /**
     * The parameter text for poseCommand, with placeholders the server fills:
     * {x} {y} (metres, robot frame), {yaw} (radians, robot frame), {yawDeg},
     * {sessionRef}. {x}, {y} and {sessionRef} are required, so the robot can refuse
     * a goal computed against another map session. Default
     * `x={x},y={y},yaw={yaw},session={sessionRef}`. Heading: the place's heading
     * through the alignment; a place with none leaves every `key={yaw}` field out
     * of the parameter (Spot then uses its nearest waypoint's heading; the Go2
     * handler requires yaw and refuses the command without moving).
     */
    poseParamTemplate: z.string().optional(),
    /**
     * The robot map session this alignment belongs to (Spot: GraphNav map_uuid; Go2:
     * `<boot_id>:<plio start>`). Approximate goals are only computed while the robot
     * reports this same session; an alignment without one is never used for them.
     */
    sessionRef: z.string().optional(),
    /** How far off an approximate goal can be, metres (from the fit RMSE or alignment quality). */
    errorM: z.number().nonnegative().optional(),
    updatedAt: z.string().datetime(),
    deletedAt: z.string().datetime().nullable().optional(),
});
/** Placeholders a poseParamTemplate may use. */
export const poseParamPlaceholders = ['x', 'y', 'yaw', 'yawDeg', 'sessionRef'];
/**
 * The default poseParamTemplate when a registration sets poseCommand alone. It
 * carries the session so the robot can check it at execution time (the Go2
 * goto_pose handler requires exactly these four keys).
 */
export const defaultPoseParamTemplate = 'x={x},y={y},yaw={yaw},session={sessionRef}';
/**
 * A robot map session as cortex accepts it (Spot `<map_id>:<hash>`, Go2
 * `<boot_id>:<epoch>`): word characters, colon, dot, hyphen.
 */
export const SessionRefSchema = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .regex(/^[\w:.-]+$/, 'A session is word characters, colon, dot or hyphen');
/**
 * A poseParamTemplate: only known {placeholders}, at most 500 chars, and it must
 * use {x} and {y} (else every place gets the same coordinates) and {sessionRef}
 * (so the robot can refuse a goal computed against another map session; both
 * the Spot adapter's map_uuid and the Go2 handler's session check use it).
 */
export const PoseParamTemplateSchema = z
    .string()
    .trim()
    .min(1)
    .max(500)
    .refine((t) => [...t.matchAll(/\{([^}]*)\}/g)].every((m) => poseParamPlaceholders.includes(m[1] ?? '')), {
    message: `Unknown placeholder; allowed: ${poseParamPlaceholders.map((p) => `{${p}}`).join(' ')}`,
})
    .refine((t) => t.includes('{x}') && t.includes('{y}'), {
    message: 'The template must use both {x} and {y}',
})
    .refine((t) => t.includes('{sessionRef}'), {
    message: 'The template must pass the session ({sessionRef}) so the robot can check it',
});
/** A command template name (e.g. `spot.waypoint.goto`), as stored on a registration. */
export const NavCommandSchema = z.string().trim().min(1).max(200);
/**
 * Optional coordinate addition to the existing Place schema. Keep Place name-first;
 * these are additive so symbolic-only consumers are unaffected. Merge these fields
 * into the existing PlaceSchema rather than creating a second schema.
 */
export const PlaceCoordinateFieldsSchema = z.object({
    /** Pose of this Place in the site frame (the one authoritative coordinate). */
    siteFramePose: SiteFramePoseSchema.optional(),
    /** Per-device native references: deviceId -> the robot's own saved waypoint id (preferred over transform). */
    perDeviceNativeWaypointId: z.record(z.string(), z.string()).optional(),
    /** An explicit GPS coordinate for this Place (outdoor waypoints; optional for indoor). */
    geoPoint: GeoPointSchema.optional(),
});
/**
 * MAP-1 (DEV-17527) API inputs for creating a device's site registration.
 * Correspondence-based: fiducial / control-points / geo-datum / prior-map all
 * reduce to a set of point correspondences the kernel solves.
 */
/** How a device reports poses, so the platform can normalize to the canonical site convention. */
export const FrameConventionSchema = z.object({
    angleUnit: z.enum(['rad', 'deg']).optional(),
    axes: z.enum(['enu', 'ned', 'xy_down']).optional(),
    metersPerUnit: z.number().positive().optional(),
});
export const RegisterFromCorrespondencesInputSchema = z.object({
    siteId: z.string(),
    deviceId: z.string(),
    level: z.number().int().optional(),
    method: RegistrationMethodSchema,
    correspondences: z.array(CorrespondenceSchema).min(2),
    /** Recover a scale term (monocular / pixel sources). Default rigid. */
    allowScale: z.boolean().optional(),
    /** Reject the fit when its RMSE exceeds this (keep the device unregistered instead). */
    maxRmse: z.number().positive().optional(),
    /**
     * How the robot-frame coordinates in `correspondences` are expressed. Applied
     * to normalize them to the canonical site convention before solving, so the
     * stored transform matches the normalized poses the map view renders.
     */
    convention: FrameConventionSchema.optional(),
    /**
     * Command template that sends this robot to its own saved waypoint (design D3).
     * Omit to keep the prior registration's; null to clear it.
     */
    navCommand: NavCommandSchema.nullable().optional(),
    /** Command template that docks this robot (empty parameter). Omit keeps, null clears. */
    dockCommand: NavCommandSchema.nullable().optional(),
    /** Command template to coordinates in its own frame (rule 2). Omit keeps, null clears. */
    poseCommand: NavCommandSchema.nullable().optional(),
    /**
     * Parameter template for poseCommand, e.g. `map_uuid={sessionRef},x={x},y={y},yaw_rad={yaw}`.
     * Omit keeps, null clears.
     */
    poseParamTemplate: PoseParamTemplateSchema.nullable().optional(),
    /**
     * Optional assertion of the robot's current map session (Spot map_uuid, Go2
     * go2.session). The server reads the session from telemetry itself and stamps
     * that: a different value here is a 409; when the read fails the registration
     * is stored without a session (exact goals only until re-aligned).
     */
    sessionRef: SessionRefSchema.optional(),
    /** Approximate-goal error, metres. Defaults to the fit RMSE when solved from correspondences. */
    errorM: z.number().nonnegative().optional(),
});
/** Manual anchor input: the operator supplies the transform directly (no solve). */
export const RegisterManualInputSchema = z.object({
    siteId: z.string(),
    deviceId: z.string(),
    level: z.number().int().optional(),
    transform: SiteTransform2DSchema,
    /**
     * Opt out of freshness aging: a pinned registration stays `live` however
     * old it is. For transforms that cannot drift, such as a sim robot whose
     * frame IS the site frame (identity). Default false: real robots age.
     * Accepted ONLY with the identity transform; anything else is a 422, so a
     * real robot's alignment cannot have its staleness caution switched off.
     */
    pinned: z.boolean().optional(),
    /**
     * Command template that sends this robot to its own saved waypoint (design D3).
     * Omit to keep the prior registration's; null to clear it.
     */
    navCommand: NavCommandSchema.nullable().optional(),
    /** Command template that docks this robot (empty parameter). Omit keeps, null clears. */
    dockCommand: NavCommandSchema.nullable().optional(),
    /** Command template to coordinates in its own frame (rule 2). Omit keeps, null clears. */
    poseCommand: NavCommandSchema.nullable().optional(),
    /**
     * Parameter template for poseCommand, e.g. `map_uuid={sessionRef},x={x},y={y},yaw_rad={yaw}`.
     * Omit keeps, null clears.
     */
    poseParamTemplate: PoseParamTemplateSchema.nullable().optional(),
    /**
     * Optional assertion of the robot's current map session (Spot map_uuid, Go2
     * go2.session). The server reads the session from telemetry itself and stamps
     * that: a different value here is a 409; when the read fails the registration
     * is stored without a session (exact goals only until re-aligned).
     */
    sessionRef: SessionRefSchema.optional(),
    /** Approximate-goal error, metres. Defaults to the fit RMSE when solved from correspondences. */
    errorM: z.number().nonnegative().optional(),
});
/**
 * PATCH a live registration without re-solving its transform: the nav, dock and
 * pose commands, the pose parameter template, the session and the error estimate. Omit a key to leave it, `null` clears it. Does not touch the
 * transform's freshness clock.
 */
export const UpdateSiteRegistrationInputSchema = z
    .object({
    navCommand: NavCommandSchema.nullable().optional(),
    dockCommand: NavCommandSchema.nullable().optional(),
    poseCommand: NavCommandSchema.nullable().optional(),
    poseParamTemplate: PoseParamTemplateSchema.nullable().optional(),
    /**
     * null clears the alignment's session (approximate goals then stop until the
     * next register/align). A session is never SET here: it belongs to a transform,
     * so it is only written together with one (register or align).
     */
    sessionRef: z
        .null()
        .describe('Only null (clears the session). A session is set only by register or align.')
        .optional(),
    errorM: z.number().nonnegative().nullable().optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Nothing to update',
});
// ── MAP-6 (DEV-17632): the backdrop map layer — the structure UNDER the dots. ──
/**
 * Where a backdrop came from. A DISPLAY map in the site frame, decoupled from any
 * robot's live navigation cost map:
 *  - FLOORPLAN: an uploaded floorplan/CAD image (source of record when present).
 *  - OCCUPANCY: a static snapshot of ONE reference robot's SLAM occupancy grid,
 *    anchored into the site frame by that robot's site_T_robot. Never auto-merged.
 *  - TILES: satellite / street tiles, positioned by a geo-anchor (outdoor sites).
 */
export const BackdropTypeSchema = z.enum(['FLOORPLAN', 'OCCUPANCY', 'TILES']);
/**
 * A site's backdrop: ONE display artifact per (site, level), in the site frame,
 * drawn under the robots + waypoints. `assetRef` is the image URL (floorplan or
 * baked occupancy grid) or a tile-template URL. The calibration
 * (metresPerPixel + siteFrameOrigin + rotationRad) is this backdrop's own
 * registration into the site frame — so a pixel maps to a site-frame metre the
 * same way a robot pose does. For TILES, `geoAnchor` positions the layer. The
 * viewer composes it with the Places and live robot poses from the same bundle.
 */
export const SiteBackdropSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    siteId: z.string(),
    /** Which site level this backdrop is for (multi-floor); 0 for single-level. */
    level: z.number().int(),
    type: BackdropTypeSchema,
    /** Operator-chosen source descriptor (an upload id or the reference deviceId), optional. */
    source: z.string().optional(),
    /** Image URL (floorplan / baked occupancy grid) or tile-template URL. */
    assetRef: z.string().optional(),
    widthPx: z.number().int().positive().optional(),
    heightPx: z.number().int().positive().optional(),
    /** Metres per backdrop pixel (image types). */
    metresPerPixel: z.number().positive().optional(),
    /** Pixel coordinate of the site-frame origin (0,0) within the image. */
    siteFrameOriginX: z.number().optional(),
    siteFrameOriginY: z.number().optional(),
    /** Backdrop rotation relative to site +X, radians. */
    rotationRad: z.number(),
    /** For TILES: the geo-anchor that positions the layer (mirrors Site.geoAnchor). */
    geoAnchor: GeoAnchorSchema.optional(),
    version: z.number().int(),
    /**
     * For a robot-map snapshot (base-map design B2): the robot it was taken from.
     *
     * DISPLAY METADATA ONLY. Stored as given and NOT checked against the site's
     * registrations or the caller's Formant device scope, because the snapshot
     * itself is uploaded by the client and nothing server-side acts on this
     * value. Whoever makes `keepInSync` drive a refresh FROM this device must
     * validate it at that point: that is the moment it stops being a label.
     */
    sourceDeviceId: z.string().optional(),
    /** The source robot's own map id/uuid at snapshot time (Spot: graphnav map_uuid). Display metadata, as sourceDeviceId. */
    sourceMapRef: z.string().optional(),
    /** Offer a refresh when the source robot reports a different map (never silent). */
    keepInSync: z.boolean(),
    /** When this version was published. */
    createdAt: z.string().datetime().optional(),
    /** Principal subject that published this version, when known. */
    createdBy: z.string().optional(),
    /** Display name (email) of the publisher, when known. */
    createdByName: z.string().optional(),
    updatedAt: z.string().datetime(),
    deletedAt: z.string().datetime().nullable().optional(),
});
/**
 * Upsert a site's backdrop (create or replace the live one for a level). The
 * calibration is validated by type at the service layer: FLOORPLAN/OCCUPANCY
 * need assetRef + metresPerPixel + siteFrameOrigin; TILES need assetRef +
 * geoAnchor. Replacing supersedes the prior live backdrop for that level.
 */
export const UpsertSiteBackdropInputSchema = z.object({
    siteId: z.string().min(1),
    level: z.number().int().optional(),
    type: BackdropTypeSchema,
    source: z.string().min(1).max(500).optional(),
    assetRef: z.string().min(1).max(2000).optional(),
    widthPx: z.number().int().positive().optional(),
    heightPx: z.number().int().positive().optional(),
    metresPerPixel: z.number().positive().optional(),
    siteFrameOriginX: z.number().optional(),
    siteFrameOriginY: z.number().optional(),
    rotationRad: z.number().optional(),
    geoAnchor: GeoAnchorSchema.optional(),
    sourceDeviceId: z.string().min(1).max(200).optional(),
    sourceMapRef: z.string().min(1).max(500).optional(),
    keepInSync: z.boolean().optional(),
});
// ── Shared-waypoint dispatch (DEV-17529 / DEV-17526, shared-waypoint-dispatch-design D1-D12) ──
/**
 * Live state a caller already knows about a robot (from telemetry), so
 * dispatch-options can flag stale robots and rank by distance (D7, D8). Cortex
 * does not read telemetry itself; without this, options are unranked by distance.
 */
export const DispatchRobotStateSchema = z.object({
    deviceId: z.string().min(1).max(200),
    /** ISO time of the robot's last position report. Older than 30 s = stale (D8). */
    lastSeenAt: z.string().datetime().optional(),
    /** The robot's latest pose in its OWN frame; lifted to the site frame via its registration. */
    pose: z.object({ x: z.number(), y: z.number(), yaw: z.number().optional() }).optional(),
    /** True when automation is blocked on this robot (e-stop, operator hold, teleop). */
    automationBlocked: z.boolean().optional(),
    // No sessionRef here, on purpose: the robot's current map session is only ever
    // read by the server from telemetry. A caller-supplied session is never trusted
    // (an unknown session reads as alignment_stale), so the field is not accepted.
    /** False when the caller knows the robot is offline. Also offline: lastSeenAt older than 5 min. */
    online: z.boolean().optional(),
    /** Name of the mission the robot is on, when known (busy exception; stub until leases ship). */
    busyWith: z.string().min(1).max(200).optional(),
});
/** POST body for dispatch-options: optional live robot state, optional device filter. */
export const DispatchOptionsQueryInputSchema = z.object({
    /** Restrict the answer to one robot. */
    deviceId: z.string().min(1).max(200).optional(),
    /**
     * `go` (default): stand at the place. `dock`: run the robot's dock command;
     * only the robot whose dock the place is can do that.
     *
     * ONE EXCEPTION, and it is deliberate: at a dock place, the robot whose dock
     * it is gets its dock command for `go` as well. Operators click their own
     * dock to send a robot home, and there is nothing to "stand at" there. For
     * every other robot `go` means stand near it, and `dock` is a
     * `dock_not_owner` refusal.
     */
    action: z.enum(['go', 'dock']).optional(),
    robots: z.array(DispatchRobotStateSchema).max(200).optional(),
    /**
     * Formant device tags the caller already has, by device id (Coherence loads
     * them with the device list). Used to evaluate the place's tag bindings and
     * skips the admin-api fetch for these devices.
     *
     * TRUSTED AS GIVEN. With a Formant token the server reads the tags itself as
     * the caller, so admin-api applies their device scope; a caller WITHOUT one
     * (a PAT, Keycloak, a service account) makes this the only source, and can
     * therefore claim tags to be handed a binding's waypoint id for a robot on
     * another map. That is a waypoint id, not a command: sending still goes
     * through issue_command and its confirm step.
     */
    deviceTags: z
        .record(z.string().min(1).max(200), DeviceTagsSchema)
        .refine((v) => Object.keys(v).length <= 500, { message: 'At most 500 devices in deviceTags' })
        .optional(),
});
/**
 * The goal a robot would be sent: `native` (its own saved waypoint, exact),
 * `metric` (the place's pose in the robot's own frame through its alignment,
 * approximate), `dock` (its dock command), or `geo`.
 */
export const DispatchGoalSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('native'), waypointId: z.string() }),
    /** Dock place that is this robot's own dock: sent as its dock command, empty parameter. */
    z.object({ kind: z.literal('dock') }),
    z.object({
        kind: z.literal('metric'),
        /** `z` (metres above the site datum) only when the place has a site-frame z (AC-8). */
        pose: z.object({
            x: z.number(),
            y: z.number(),
            yaw: z.number().optional(),
            z: z.number().optional(),
        }),
    }),
    z.object({
        kind: z.literal('geo'),
        lat: z.number(),
        lon: z.number(),
        alt: z.number().optional(),
        /** The place's site-frame z, when it has one (never folded into `alt`). */
        altAboveSiteDatum: z.number().optional(),
        derived: z.boolean().optional(),
    }),
]);
/** One robot's answer to "how would you get to this place". Never sends anything (D2). */
/** The only "can't" a person sees (any-robot-any-place design, exception table). */
export const DispatchReasonCodeSchema = z.enum([
    'offline',
    'not_registered',
    'alignment_stale',
    'alignment_failed',
    'no_pose_command',
    'no_nav_command',
    'no_dock_command',
    'dock_not_owner',
    'place_not_on_map',
    'tags_unknown',
    'busy',
]);
export const DispatchOptionSchema = z.object({
    deviceId: z.string(),
    eligible: z.boolean(),
    /**
     * Not eligible: the one plain-English exception (see reasonCode). Eligible: an
     * optional caution (no recent position, automation blocked).
     */
    reason: z.string().optional(),
    /** Set when not eligible: which exception it is. */
    reasonCode: DispatchReasonCodeSchema.optional(),
    /** `exact` = the robot's own saved spot (or its dock); `approx` = through its map alignment. */
    precision: z.enum(['exact', 'approx']).optional(),
    /** For approx: how far off the goal can be, metres ("Approx. ±X m"). */
    errorM: z.number().nonnegative().optional(),
    goal: DispatchGoalSchema.optional(),
    /** The exact command to send through issue_command (eligible options only). */
    command: z.object({ templateName: z.string(), parameter: z.string() }).optional(),
    registrationStatus: RegistrationStatusSchema,
    /** True when the caller reported no position for more than 30 s (D8). */
    stale: z.boolean().optional(),
    /** Straight-line site-frame distance from the robot to the place, when both are known. */
    distanceM: z.number().optional(),
    /** 1-based; eligible robots first, best first (D7). */
    rank: z.number().int().positive(),
});
export const DispatchOptionsResultSchema = z.object({
    place: z.object({ id: z.string(), label: z.string(), siteId: z.string() }),
    options: z.array(DispatchOptionSchema),
});
/** find_places (D5): one ranked candidate for what a person typed. */
export const PlaceSearchMatchSchema = z.object({
    placeId: z.string(),
    label: z.string(),
    siteId: z.string(),
    siteName: z.string(),
    /** Similarity in [0,1]; 1 = exact label or alias match (case-insensitive). */
    score: z.number(),
    /** What matched: the label itself, or one of the place's aliases. */
    matchedOn: z.enum(['label', 'alias']),
    /** The alias text when matchedOn is 'alias'. */
    matchedAlias: z.string().optional(),
    aliases: z.array(z.string()),
});
export const PlaceSearchResultSchema = z.object({
    query: z.string(),
    matches: z.array(PlaceSearchMatchSchema),
});
// ── Base-map upload + calibration (base-map-upload-design B1, B3) ──
/** Largest backdrop image accepted, in bytes (15 MB). */
export const siteBackdropAssetMaxBytes = 15 * 1024 * 1024;
/** Per-org backdrop image quota: count and total bytes. */
export const siteBackdropAssetOrgMaxCount = 200;
export const siteBackdropAssetOrgMaxBytes = 2 * 1024 * 1024 * 1024;
/** Image types a backdrop asset may be. Checked by sniffing magic bytes, not trusted from the client. */
export const BackdropImageContentTypeSchema = z.enum(['image/png', 'image/jpeg', 'image/webp']);
/** The `assetRef` prefix that points a SiteBackdrop at a cortex-stored asset. */
export const siteBackdropAssetRefPrefix = 'cortex://site-backdrop-assets/';
/**
 * Upload a backdrop image as base64 inside JSON (no multipart dependency; fits the
 * API's 25 MB JSON body limit: 15 MB of image is about 20 MB of base64). The type
 * is decided by the file's magic bytes; `contentType`, when given, must agree.
 */
export const UploadSiteBackdropAssetInputSchema = z.object({
    siteId: z.string().min(1),
    /** The image file, base64 (standard or URL-safe alphabet; a data: URL prefix is allowed). */
    // No length cap here on purpose: the service measures the DECODED size and
    // answers 413 (after stripping a data: prefix and whitespace), and the JSON body
    // limit bounds the request itself.
    dataBase64: z.string().min(1),
    /** Informational only: the type sniffed from the file wins when they disagree. */
    contentType: BackdropImageContentTypeSchema.optional(),
});
/** Stored asset metadata (never the bytes). */
export const SiteBackdropAssetSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    siteId: z.string(),
    contentType: BackdropImageContentTypeSchema,
    sizeBytes: z.number().int().nonnegative(),
    sha256: z.string(),
    /** Pixel size parsed from the file header. */
    widthPx: z.number().int().positive(),
    heightPx: z.number().int().positive(),
    /** Put this in SiteBackdrop.assetRef: `cortex://site-backdrop-assets/<id>`. */
    assetRef: z.string(),
    createdAt: z.string().datetime(),
});
/** One calibration pin: a pixel on the image and the site-frame point (metres) it shows. */
export const BackdropCalibrationPairSchema = z.object({
    /** Image pixel, origin top-left, x right, y DOWN (as the image is stored). */
    px: z.object({ x: z.number(), y: z.number() }),
    /** Site frame, metres, x right, y UP. */
    site: z.object({ x: z.number(), y: z.number() }),
});
export const CalibrateSiteBackdropInputSchema = z.object({
    pairs: z
        .array(BackdropCalibrationPairSchema)
        .min(2, {
        message: 'Calibration needs at least 2 pins (3 or more also give a quality estimate)',
    })
        .max(100),
    /**
     * Solve the scale (metres per pixel). Must be true or omitted: an image's pixel
     * size is never metres, so a rigid (scale 1) fit is refused.
     */
    allowScale: z
        .boolean()
        .optional()
        .refine((v) => v !== false, {
        message: 'allowScale: false is not supported for image backdrops (pixels are not metres)',
    }),
});
/**
 * The calibration, in the SiteBackdrop field convention. With s = metresPerPixel,
 * th = rotationRad and (ox, oy) = siteFrameOrigin (pixels, y down), a site point
 * (X, Y) draws at pixel
 *   u = ox + ( cos(th) * X + sin(th) * Y) / s
 *   v = oy - (-sin(th) * X + cos(th) * Y) / s
 * i.e. px = origin + R(-th) * (site / s) with the y component flipped. Equivalently
 * the image is drawn by flipping y, scaling by s and rotating by th about the site
 * origin; th is the CCW angle of the image's +x axis from site +x.
 */
export const SiteBackdropCalibrationSchema = z.object({
    metresPerPixel: z.number().positive(),
    siteFrameOriginX: z.number(),
    siteFrameOriginY: z.number(),
    rotationRad: z.number(),
    /** Root-mean-square pin error, metres. */
    rmse: z.number().nonnegative(),
    /** Each pin's error in metres, in the order given. */
    perPairErrorM: z.array(z.number().nonnegative()),
});
// ── Auto-alignment (any-robot-any-place design, "Keeping alignments current") ──
/** POST /v1/sites/:siteId/registrations/:deviceId/align body. */
export const AlignRegistrationInputSchema = z.object({
    /** Site level (default 0): the base map of this level is the reference. */
    level: z.number().int().optional(),
    /**
     * `previous`: seed the search with the robot's current registration, for a
     * robot that booted on its dock. Needs bootedOnDock: true, and the server must
     * see the robot within 1 m of its dock place through that registration. The
     * answer must lie within 15 deg and 2 m of it AND beat the best match outside
     * that window by 1.15. `none` (default): search every rotation.
     */
    seed: z.enum(['none', 'previous']).optional(),
    /** The caller's assertion that the robot booted on its dock (required for seed=previous). */
    bootedOnDock: z.boolean().optional(),
    /**
     * Optional assertion of the robot's current map session. The server always
     * reads the session from telemetry itself; when this is given and differs (or
     * the session cannot be read), the request is refused with 409.
     */
    sessionRef: SessionRefSchema.optional(),
    /**
     * Required with `?apply=true` when the robot already has a live alignment and
     * the new transform differs from it by more than
     * {@link maxUnconfirmedAlignmentShiftM} / {@link maxUnconfirmedAlignmentRotationDeg}.
     *
     * An accepted match is not necessarily the right one: look-alike rooms score
     * within a few percent of the truth, so a match that clears the margin can
     * still be a different rotation or shift, and the stored alignment is
     * immediately trusted for approximate goals. A big jump away from an
     * alignment someone already established is exactly the case a person should
     * see the overlay for first, so without this the answer is
     * `applied: false` with the overlay and a rejectReason.
     */
    confirmReplace: z.boolean().optional(),
});
/** How far a new auto-alignment may move the robot's origin before a person confirms. */
export const maxUnconfirmedAlignmentShiftM = 1;
/** How far it may rotate before a person confirms. */
export const maxUnconfirmedAlignmentRotationDeg = 5;
export const AlignRegistrationResultSchema = z.object({
    /** True when the match is clear enough to trust (margin and overlap thresholds). */
    accepted: z.boolean(),
    /** True when ?apply=true and accepted: the registration was stored. */
    applied: z.boolean(),
    /** site = R(rotationRad) * robot + (tx, ty); scale 1. */
    transform: SiteTransform2DSchema,
    rotationDeg: z.number(),
    /**
     * Unseeded: best / best at a rotation more than 20 deg away. Seeded: best inside
     * the seed window / best outside it. Both refined the same way; 1.15 to accept.
     */
    margin: z.number(),
    runnerUpRotationDeg: z.number().optional(),
    /** Share of the robot's obstacles (inside the base map) within 0.15 m of a site obstacle. */
    overlap: z.number(),
    /** Share of ALL the robot's obstacles that land inside the base map (30% to accept). */
    insideShare: z.number(),
    /**
     * Alignment error in metres, as a bound rather than an average: the 90th
     * percentile of the distance from each of the robot's considered obstacles to
     * the nearest site obstacle. An RMS over the close points alone would read as
     * a few centimetres while most of the evidence was metres off, and this value
     * becomes the registration's `errorM`, which operators and the chat model see
     * as "about N metres" for approximate goals.
     */
    errorM: z.number(),
    seeded: z.boolean(),
    /** Plain reason when not accepted ("needs manual alignment"). */
    rejectReason: z.string().optional(),
    /** The robot session the alignment belongs to, when known. */
    sessionRef: z.string().optional(),
    /**
     * True when applied: an alignment is only stored with the robot's current
     * session, so a stored one can be used for approximate goals.
     */
    approxReady: z.boolean(),
    /** Which localization stream the robot map came from. */
    robotMapStream: z.string(),
    /** When that robot map datapoint was recorded (at most 5 minutes old, in the current session). */
    robotMapAt: z.string().datetime(),
    /** Where the robot is now on the site map, under this alignment. */
    robotSitePose: z.object({ x: z.number(), y: z.number() }).optional(),
    /** data:image/png;base64 overlay: site obstacles grey, robot obstacles orange, robot violet. */
    overlayPng: z.string(),
    /** The stored registration when applied. */
    registration: DeviceSiteRegistrationSchema.optional(),
});
//# sourceMappingURL=places.js.map